// API クライアント

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  APIResponse,
  APIError,
  getApiConfig,
  UploadProgressEvent,
  AIQuotaCheckResponse,
} from '../types';

export interface AIConsultRequest {
  question: string;
  session_id?: string;
  tenant_id?: string;
  current_section?: string;
  current_field?: string;
  form_data?: Record<string, string>;
  bcp_type?: string;
}

export interface AIConsultReference {
  text: string;
  source: string;
  score: string;
}

export interface AIConsultResponse {
  success: boolean;
  answer: string;
  references?: AIConsultReference[];
  context_used?: Record<string, string>;
  error?: string;
}

class APIClient {
  private client: AxiosInstance;
  private csrfToken: string | null = null;

  constructor() {
    // Get config dynamically to ensure env vars are evaluated at runtime
    const config = getApiConfig();

    // Log the API base URL for debugging
    console.log('API Base URL:', config.baseURL);

    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: config.headers,
    });

    this.setupInterceptors();

    // Initialize CSRF token asynchronously
    void this.initCsrfToken();
  }

  private async initCsrfToken(): Promise<void> {
    try {
      const response = await this.client.get<{ csrf_token: string }>('/auth/csrf-token');
      this.csrfToken = response.data.csrf_token;
      console.log('CSRF token initialized');
    } catch (error) {
      console.warn('Failed to initialize CSRF token:', error);
      // Don't throw error - CSRF token will be fetched on demand
    }
  }

  async refreshCsrfToken(): Promise<string> {
    try {
      const response = await this.client.get<{ csrf_token: string }>('/auth/csrf-token');
      this.csrfToken = response.data.csrf_token;
      console.log('CSRF token refreshed');
      return this.csrfToken;
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error);
      this.csrfToken = null;
      throw error;
    }
  }

  private setupInterceptors() {
    // リクエストインターセプター
    this.client.interceptors.request.use(
      async (config) => {
        // リクエスト前の処理（認証トークン付与など）
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        console.log(`Request baseURL: ${config.baseURL}`);
        console.log(`Request full URL: ${config.baseURL}${config.url}`);
        console.log(`Request headers:`, config.headers);

        // Add CSRF token to non-GET requests
        const method = config.method?.toUpperCase();
        if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
          // If CSRF token is not available, try to fetch it
          if (!this.csrfToken) {
            console.warn('CSRF token not available, fetching now...');
            try {
              await this.refreshCsrfToken();
            } catch (error) {
              console.error('Failed to fetch CSRF token:', error);
              // Continue without token - will likely fail with 403
            }
          }

          if (this.csrfToken) {
            config.headers['X-CSRF-Token'] = this.csrfToken;
            console.log('CSRF token added to request');
          } else {
            console.warn('CSRF token still not available for request');
          }
        }

        return config;
      },
      (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
      }
    );

    // レスポンスインターセプター
    this.client.interceptors.response.use(
      (response: AxiosResponse<APIResponse>) => {
        console.log(`API Response: ${response.status} ${response.config.url}`);
        console.log(`Response Content-Type: ${response.headers['content-type']}`);
        console.log(`Response data type: ${typeof response.data}`);
        console.log(`Response data preview:`, response.data?.toString().substring(0, 200));
        return response;
      },
      (error) => {
        console.error('Response Error:', error);

        // Handle AI quota exceeded errors (429)
        if (error.response?.status === 429) {
          console.log('429 error detected, response data:', error.response?.data);
          const detail = error.response?.data?.detail;
          console.log('Detail object:', detail);
          // Dispatch custom event for quota exceeded
          if (detail && typeof detail === 'object') {
            // Check if this is a quota limit error (not just rate limiting)
            if (
              detail.accepted_tokens !== undefined ||
              detail.message?.includes('クォータ')
            ) {
              console.log('Dispatching ai-quota-exceeded event');
              const event = new CustomEvent('ai-quota-exceeded', {
                detail: {
                  quotaWarning: detail,
                  response: error.response?.data,
                },
              });
              window.dispatchEvent(event);
              console.log('ai-quota-exceeded event dispatched');
            } else {
              console.log('Quota condition not met, event not dispatched');
            }
          } else {
            console.log('Detail is not an object, event not dispatched');
          }
        }

        // Handle authentication errors (401) and authorization errors (403)
        if (error.response?.status === 401 || error.response?.status === 403) {
          // 開発環境または認証が無効の場合はリダイレクトしない
          const isDevelopment = import.meta.env.DEV;
          const authEnabled = import.meta.env.VITE_ENABLE_AUTHENTICATION === 'true';
          const currentPath = window.location.pathname;

          // エラーページにいる場合はリダイレクトしない
          const isErrorPage = currentPath === '/error';

          if (isDevelopment || !authEnabled || isErrorPage) {
            console.warn('Session invalid or expired (auth disabled in development or on error page)');
          } else {
            console.warn('Session invalid or expired, redirecting to login...');
            // Import getLoginRedirectUrl dynamically to avoid circular dependencies
            const toshoMyPageUrl = import.meta.env.VITE_TOSHO_MYPAGE_URL || 'https://01.tokyocci-test.prod.powercms.cloud';
            const baseUrl = window.location.origin;
            const redirectUrl = `${baseUrl}/auth/start?redirect=${currentPath}`;
            const encodedUrl = encodeURIComponent(redirectUrl);
            window.location.href = `${toshoMyPageUrl}/?logout=1&bcp_redirect_url=${encodedUrl}`;
          }
        }

        const apiError: APIError = {
          code: error.response?.status?.toString() || 'UNKNOWN',
          message: error.response?.data?.message || error.message || 'An unknown error occurred',
          details: error.response?.data,
          timestamp: new Date().toISOString(),
        };

        return Promise.reject(apiError);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    const response = await this.client.get<APIResponse<T>>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    const response = await this.client.post<APIResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    const response = await this.client.put<APIResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    const response = await this.client.delete<APIResponse<T>>(url, config);
    return response.data;
  }

  async getRaw<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // Return raw Axios response (includes headers, status, etc.)
    return await this.client.get<T>(url, config);
  }

  async postRaw<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // Return raw Axios response (includes headers, status, etc.)
    return await this.client.post<T>(url, data, config);
  }

  async uploadFiles(
    files: File[],
    onProgress?: (event: UploadProgressEvent) => void
  ): Promise<APIResponse<any>> {
    if (files.length === 0) {
      throw new Error('No files provided');
    }

    // Upload multiple files sequentially (not in parallel)
    const results = [];
    let totalUploaded = 0;
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    let sessionId: string | null = null;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      
      // Add session_id if we have one from previous upload
      if (sessionId) {
        formData.append('session_id', sessionId);
      }

      const config: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            // Calculate overall progress across all files
            const currentFileProgress = progressEvent.loaded;
            const overallProgress = totalUploaded + currentFileProgress;
            const percentage = Math.round((overallProgress * 100) / totalSize);
            
            onProgress({
              loaded: overallProgress,
              total: totalSize,
              percentage,
            });
          }
        },
      };

      try {
        console.log(`Uploading file ${i + 1}/${files.length}: ${file.name}`);
        const result = await this.post('/api/upload-bcp', formData, config);
        results.push(result);
        totalUploaded += file.size;
        
        // Save session_id from first upload for subsequent files
        if (!sessionId && result.data?.session_id) {
          sessionId = result.data.session_id;
          console.log('Using session ID:', sessionId);
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        throw error;
      }
    }
    
    // Merge all extracted info from all files
    const mergedExtractedInfo: any = {
      company_name: null,
      address: null,
      tel: null,
      fax: null,
      clients: [],
      suppliers: []
    };
    
    // Merge extracted info from all uploaded files
    for (const result of results) {
      if (result.data?.extracted_info) {
        const info = result.data.extracted_info;
        
        // Use the first non-null company_name found
        if (!mergedExtractedInfo.company_name && info.company_name) {
          mergedExtractedInfo.company_name = info.company_name;
        }
        
        // Use the first non-null address found
        if (!mergedExtractedInfo.address && info.address) {
          mergedExtractedInfo.address = info.address;
        }
        
        // Use the first non-null tel found
        if (!mergedExtractedInfo.tel && info.tel) {
          mergedExtractedInfo.tel = info.tel;
        }
        
        // Use the first non-null fax found
        if (!mergedExtractedInfo.fax && info.fax) {
          mergedExtractedInfo.fax = info.fax;
        }
        
        // Merge clients (avoid duplicates)
        if (info.clients && Array.isArray(info.clients)) {
          for (const client of info.clients) {
            if (!mergedExtractedInfo.clients.includes(client)) {
              mergedExtractedInfo.clients.push(client);
            }
          }
        }
        
        // Merge suppliers (avoid duplicates)
        if (info.suppliers && Array.isArray(info.suppliers)) {
          for (const supplier of info.suppliers) {
            if (!mergedExtractedInfo.suppliers.includes(supplier)) {
              mergedExtractedInfo.suppliers.push(supplier);
            }
          }
        }
      }
    }
    
    // Return the last result which should contain backend's merged info
    const finalResult = results[results.length - 1];
    if (finalResult.data) {
      // Use backend's merged info if available, otherwise use our client-side merge
      if (finalResult.data.merged_extracted_info) {
        finalResult.data.extracted_info = finalResult.data.merged_extracted_info;
        console.log('Using backend merged info:', finalResult.data.merged_extracted_info);
      } else {
        finalResult.data.extracted_info = mergedExtractedInfo;
        console.log('Using client-side merged info:', mergedExtractedInfo);
      }
      finalResult.data.all_upload_results = results; // Keep all results for reference
    }
    
    return finalResult;
  }

  async generateBCP(data: any): Promise<APIResponse<any>> {
    const config = {
      timeout: 600000, // 10 minutes for LLM processing
    };
    return this.post('/api/bcp/generate', data, config);
  }

  async exportBCP(data: any, format: string): Promise<APIResponse<any>> {
    return this.post('/api/bcp/export', { ...data, format });
  }

  async healthCheck(): Promise<APIResponse<any>> {
    return this.get('/api/health');
  }

  async consultAI(request: AIConsultRequest): Promise<AIConsultResponse> {
    const response = await this.client.post<AIConsultResponse>(
      '/api/ai-consult',
      request
    );
    return response.data;
  }

  async extractCompanyInfoFromUrl(url: string): Promise<ExtractFromUrlResponse> {
    const response = await this.client.post<ExtractFromUrlResponse>(
      '/api/extract-from-url',
      { url }
    );

    // Normalize null to empty string for optional fields
    if (response.data.data?.employee_count === null) {
      response.data.data.employee_count = '';
    }

    return response.data;
  }

  async generateStockpile(data: {
    company_info?: string;
    employees?: string;
    business_type?: string;
    location?: string;
    critical_business?: string;
  }): Promise<{ stockpile_plan: string }> {
    const response = await this.client.post<{ stockpile_plan: string }>(
      '/api/bcp/generate-stockpile',
      data
    );
    return response.data;
  }

  async submitInquiry(data: InquiryRequest): Promise<InquiryResponse> {
    try {
      const response = await this.client.post<InquiryResponse>(
        '/api/inquiry',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Inquiry API error:', error);
      // Return error response
      return {
        success: false,
        error: 'ネットワークエラーが発生しました。もう一度お試しください。',
      };
    }
  }

  async checkAIQuota(): Promise<AIQuotaCheckResponse> {
    const response = await this.client.get<AIQuotaCheckResponse>(
      '/api/ai/quota/check'
    );
    return response.data;
  }
}

// Export the class for direct use
export { APIClient };

// シングルトンインスタンス
export const apiClient = new APIClient();

// 便利な関数をエクスポート
export const uploadFiles = (files: File[], onProgress?: (event: UploadProgressEvent) => void) =>
  apiClient.uploadFiles(files, onProgress);

export const generateBCP = (data: any) =>
  apiClient.generateBCP(data);

export const exportBCP = (data: any, format: string) =>
  apiClient.exportBCP(data, format);

export const healthCheck = () =>
  apiClient.healthCheck();

export const consultAI = (request: AIConsultRequest) =>
  apiClient.consultAI(request);

export interface ExtractFromUrlRequest {
  url: string;
}

export interface ExtractFromUrlResponse {
  success: boolean;
  data?: {
    company_name: string;
    location: string;
    employee_count: string | null;
    business_description: string;
  };
  error?: string;
  message?: string;
}

export const extractCompanyInfoFromUrl = (url: string) =>
  apiClient.extractCompanyInfoFromUrl(url);

export const generateStockpile = (data: {
  company_info?: string;
  employees?: string;
  business_type?: string;
  location?: string;
  critical_business?: string;
}) => apiClient.generateStockpile(data);

export interface InquiryRequest {
  name: string;
  company: string;
  email: string;
  message: string;
}

export interface InquiryResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const submitInquiry = (data: InquiryRequest) =>
  apiClient.submitInquiry(data);

export const checkAIQuota = () =>
  apiClient.checkAIQuota();
